import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Clock, DollarSign, User, Sparkles, ChevronDown } from 'lucide-react';
import { allServices, BOOKING_URL } from '../data/services';

export const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = allServices.find(s => s.id === serviceId);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <main className="pt-24 min-h-screen bg-chalk flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-dark mb-4">Service Not Found</h1>
          <Link to="/services" className="text-amaranth hover:underline">
            View All Services
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid={`service-detail-${serviceId}`} className="bg-chalk min-h-screen">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={service.heroImage || service.image}
          alt={service.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark/70 via-dark/30 to-transparent" />
        
        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-16">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/services"
                data-testid="back-to-services"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft size={18} />
                <span className="text-sm">All Services</span>
              </Link>
              
              <span className={`inline-block px-3 py-1 text-xs text-white rounded-full mb-4 ${
                service.category === 'beauty' ? 'bg-amaranth' : 'bg-brook'
              }`}>
                {service.category === 'beauty' ? 'Beauty' : 'Wellness'}
              </span>
              
              <h1 className="font-serif text-white text-4xl lg:text-6xl mb-4">
                {service.name}
              </h1>
              
              <p className="text-white/80 text-lg max-w-2xl">
                {service.intro}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-12">
              {/* What It Treats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h2 className="font-serif text-dark text-2xl mb-6">What It Treats</h2>
                <div className="flex flex-wrap gap-3">
                  {service.treats.map((item, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-light text-dark-secondary text-sm"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h2 className="font-serif text-dark text-2xl mb-6">Benefits</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check size={18} className="text-amaranth mt-0.5 flex-shrink-0" />
                      <span className="text-dark-secondary">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Ideal Candidate */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h2 className="font-serif text-dark text-2xl mb-6 flex items-center gap-3">
                  <User size={24} className="text-thulian" />
                  Ideal Candidate
                </h2>
                <p className="text-dark-secondary leading-relaxed">
                  {service.idealCandidate}
                </p>
              </motion.div>

              {/* Treatment Process */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <h2 className="font-serif text-dark text-2xl mb-6 flex items-center gap-3">
                  <Sparkles size={24} className="text-thulian" />
                  Treatment Process
                </h2>
                <p className="text-dark-secondary leading-relaxed">
                  {service.process}
                </p>
              </motion.div>

              {/* Downtime */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <h2 className="font-serif text-dark text-2xl mb-6 flex items-center gap-3">
                  <Clock size={24} className="text-thulian" />
                  Downtime & Recovery
                </h2>
                <p className="text-dark-secondary leading-relaxed">
                  {service.downtime}
                </p>
              </motion.div>

              {/* Variations (if any) */}
              {service.variations && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <h2 className="font-serif text-dark text-2xl mb-6">Treatment Options</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.variations.map((variant, index) => (
                      <div
                        key={index}
                        className="p-6 bg-light border-l-4 border-thulian"
                      >
                        <h4 className="font-serif text-dark text-lg mb-2">
                          {variant.name}
                        </h4>
                        <p className="text-dark-secondary text-sm mb-2">
                          {variant.desc}
                        </p>
                        {variant.price && (
                          <p className="text-amaranth font-medium">
                            {variant.price}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* FAQs Section */}
              {service.faqs && service.faqs.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="mt-12"
                >
                  <h2 className="font-serif text-dark text-2xl mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-3">
                    {service.faqs.map((faq, index) => (
                      <div
                        key={index}
                        className="bg-light overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFaq(openFaq === index ? null : index)}
                          data-testid={`faq-${index}`}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-chalk/50 transition-colors"
                        >
                          <span className="font-medium text-dark pr-4">{faq.q}</span>
                          <ChevronDown 
                            size={20} 
                            className={`text-amaranth flex-shrink-0 transition-transform duration-300 ${
                              openFaq === index ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {openFaq === index && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <p className="px-5 pb-5 text-dark-secondary leading-relaxed">
                                {faq.a}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="sticky top-32 bg-light p-8"
              >
                {/* Price */}
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-dark/10">
                  <DollarSign size={24} className="text-amaranth" />
                  <div>
                    <p className="text-dark-secondary text-sm">Price</p>
                    <p className="font-serif text-dark text-2xl">
                      {service.startingPrice}
                    </p>
                  </div>
                </div>

                {/* Book Button */}
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-now-service"
                  className="btn-luxury block w-full bg-amaranth text-white text-center py-4 rounded-full font-medium mb-4"
                >
                  Book This Treatment
                </a>

                <p className="text-dark-secondary text-xs text-center">
                  Complimentary consultation included
                </p>

                {/* Contact Info */}
                <div className="mt-8 pt-6 border-t border-dark/10 text-center">
                  <p className="text-dark-secondary text-sm mb-2">Questions?</p>
                  <a
                    href="tel:(424) 777-5458"
                    className="text-amaranth font-medium hover:underline"
                  >
                    (424) 777-5458
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 px-6 bg-light">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-dark text-3xl mb-8 text-center">
            You May Also Like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {allServices
              .filter(s => s.id !== serviceId && s.category === service.category)
              .slice(0, 3)
              .map((relatedService) => (
                <Link
                  key={relatedService.id}
                  to={`/services/${relatedService.id}`}
                  data-testid={`related-${relatedService.id}`}
                  className="group"
                >
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img
                      src={relatedService.image}
                      alt={relatedService.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="font-serif text-dark text-xl group-hover:text-amaranth transition-colors">
                    {relatedService.name}
                  </h3>
                  <p className="text-dark-secondary text-sm">
                    {relatedService.shortDesc}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetailPage;
